import { LitElement, html, css } from 'lit';
import { msg,str } from '@lit/localize';
import { accessibility,formStyles,grid } from '../../utilities/styles';
import { cardLinks } from '../../utilities/accessibility';
import { arrowBack } from '../../utilities/icons';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { AllInOrganisationConsumer } from '../Utilities/AllInOrganisationConsumer.js';
import { AllInIntegrationsBase } from '../Utilities/AllInIntegrationsBase.js';

export class AllInIntegrationsManager extends AllInIntegrationsBase(AllInOrganisationConsumer(LitElement)) {
    static properties = {
        ...super.properties,
        _hasConfig: {state:true,type:Boolean},
    }

    constructor() {
        super();
    }

    async connectedCallback() {
        super.connectedCallback();
        this.loading = true;
        if(this.organisation.hasOwnProperty('integration_config')&&this.organisation.integration_config){
            this.providerConfig = this.organisation.integration_config;
            this.providerConfigMapping = this.organisation.integration_config.mappings;
            this._updateContext({providerConfig:this.providerConfig, providerConfigMapping:this.providerConfigMapping});
            this.loading=false;
        }
        else{
            await this._getTicketingProviderConfig();
        }
        this._hasConfig = Object.keys(this.providerConfig ?? {}).length > 0;
        if(this.panel){
            this._showPanel(this.panel,this.view);
        }
        
        this.renderRoot.querySelector('button,a')?.focus();
        cardLinks(this.shadowRoot);
    }
    
    disconnectedCallback(){
        super.disconnectedCallback();
        this.eventManager.disconnect();
    }

    updated(changedProperties){
        if(changedProperties.has('panel')||changedProperties.has('view')){
            this.renderRoot.querySelector('button,a')?.focus();
        }
        if(changedProperties.has('providerConfig')){
            this._hasConfig = Object.keys(this.providerConfig ?? {}).length > 0;
        }
        cardLinks(this.shadowRoot);
    }

    render() {
        if(this.loading){
            return html`<allin-loader loading></allin-loader>`;
        }
        let main='';
        let button = html`<button class="back" @click=${this._hidePanel}><span aria-hidden="true">${arrowBack}</span>${msg(str`Return to all integrations`)}</button>`;
        if(this.panel=='ticketing-provider-configuration'){
            main=html`
                ${button}
                <allin-organisation-integration-form currentOrg=${this.currentOrg}></allin-organisation-integration-form>
            `;
        }
        else if(this.panel=='event-configuration'&&this._hasConfig){
            main=html`
                ${button}
                <allin-organisation-event-integration-form currentOrg=${this.currentOrg}></allin-organisation-event-integration-form>
            `;
        }
        else if(this.panel=='member-configuration'&&this._hasConfig){
            main=html`
                ${button}
                <allin-organisation-member-integration-form currentOrg=${this.currentOrg}></allin-organisation-member-integration-form>
            `;
        }
        else{
            let memberText = '';
            let memberDisabled=false;
            let tier = this.organisation.subscriptionTier;
            if(tier=='Tier3'){
                if(this.providerConfig?.provider=='TicketsolveXML'||this.providerConfig?.provider=='TicketsolveJSON'){
                    memberDisabled=true;
                    memberText = msg('Your current ticketing provider does not support member data integration.');
                }
                else if(this.providerConfig?.provider=='DMS'||this.providerConfig?.usesActionLinks){
                    memberDisabled=true;
                    memberText = msg('You are using Action Links for your Membership Integration');
                }
                else{
                    memberText = this._hasConfig?
                    html`<a href="javascript:void(0)" class="card_target" @click=${(e)=>this._showPanel('member-configuration')}>${msg('Manage member data')}</a>`:
                    html`<p class="disabled_message">${msg('You need to configure your ticketing provider before you can manage member data')}</p>`;          
                    memberDisabled=!this._hasConfig;
                }
            }
            else{
                memberDisabled=true;
                memberText = msg('Your current subscription does not support member data integration.');
            }
            let eventText = '';
            let eventDisabled = false;
            if(this._hasConfig){
                if(this.providerConfig?.provider=='DMS'){
                    eventDisabled = true;
                    eventText = msg(`Your ticketing provider doesn't support event listing integration.`);
                }
                else{
                    eventText = html`<a href="javascript:void(0)" class="card_target" @click=${(e)=>this._showPanel('event-configuration')}>${msg('Manage event listing data')}</a>`;
                }
            }
            else{
                eventDisabled = true;
                eventText = html`<p class="disabled_message">${msg('You need to configure your ticketing provider before you can manage event listing data')}</p>`

            }
            main=html`
                <p class="has-margin">${unsafeHTML(this.integration_panels?.integrations_intro_text)}</p>
                <div class="grid grid_50">
                    <div class="bg inner-padding card">
                        <h3>${msg('Ticketing provider integration')}</h3>
                        <p>${unsafeHTML(this.integration_panels?.integrations_ticketing_provider_description_text)}</p>
                        <a href="javascript:void(0)" class="card_target" @click=${(e)=>this._showPanel('ticketing-provider-configuration')}>${msg('Manage ticketing provider data')}</a>
                    </div>
                    <div class="bg inner-padding card ${eventDisabled?'disabled':''}">
                        <h3>${msg('Event listing integration')}</h3>
                        <p>${unsafeHTML(this.integration_panels?.integrations_event_listing_description_text)}</p>
                        ${eventText}
                    </div>
                    <div class="bg inner-padding card ${memberDisabled?'disabled':''}">
                        <h3>${msg('Member integration')}</h3>
                        <p>${unsafeHTML(this.integration_panels?.integrations_member_description_text)}</p>
                        ${memberText}
                    </div>
                </div>
            `;
        }
        return html`${main}`;
    }

    


    _getOrganisationById(id){
        return this.organisations.find((org)=>org.org_id==id);
    }

    static styles = [
        accessibility,
        formStyles,
        grid,
        css`
        allin-greeting{
            --allin-font-size: 2rem;
            --allin-font-weight: 700;
        }
        .bg{
            padding:var(--allin-padding,2rem);
            background-color:var(--allin-bg-color,#ccc);
            display:grid;
            gap:1rem;
        }
        .small{
            font-size:0.875rem;
        }

        .grid{
            gap:1.5rem;
        }
        
        .card a{
            display:block;
            padding:1rem;
            border-radius:var(--allin-border-radius,0.5rem);
            background-color:var(--allin-highlight-color,#472961);
            color:#fff;
            text-align:center;
            font-weight:700;
            text-decoration:none;
            transition:background-color var(--transition) ease;
            height:fit-content;
            align-self:end;
        }

        .card:hover a{
            background-color:var(--allin-highlight-color-lighter,#5e3e7d);
        }


        h3,p{
            margin:0;
        }
        
        p.has-margin{
            margin-bottom:1.5rem;
        }

        fieldset{
            border:0;
            padding:0;
            margin:0;
        }

        legend{
            font-size:1.25rem;
            font-weight:700;
            margin-bottom:1rem;
            color:var(--allin-highlight-color,#472961);
        }
        legend:has(+p){
            margin-bottom:0.5rem;
        }

        .button-group{
            display:flex;
            justify-content:space-between;
            margin-top:2rem;
        }

        .border-top{
            border-top:1px solid var(--allin-highlight-color,#472961);
            padding-top:2rem;
        }
        .button-group button, .return{
            background-color:transparent;
            color:var(--allin-highlight-color,#472961);
            border:0;
            display:flex;
            gap:0.5rem;
            align-items:center;
            text-decoration:underline;
            cursor:pointer;
            transition:color var(--transition) ease;
            font-weight:700;
            font-size:1rem;

        }
        .return{
            padding:0;
            margin-bottom:2rem;
        }

        .button-group button svg, .return svg{
            transition:transform var(--transition) ease;
        }
        .button-group button:hover, .return:hover{
            text-decoration-thickness:2px;
        }
        
        .button-group button:hover svg, .return:hover svg{
            transform:rotate(15deg);
        }
        
        table{
            width:100%;
            border-collapse:collapse;
        }

        td{
            padding-bottom:2rem;
            word-break:break-all;
        }

        td:first-child{
            width:40%;
            padding-right:2rem;
            vertical-align:top;
        }

        td:last-child{
            width:60%;
            font-weight:600;
            vertical-align:top;
        }
        
        td svg{
            height:0.5rem;
        }
        ul{
            list-style:none;
            padding:0;
            margin:0;
        }

        button.back{
            color:var(--allin-highlight-color,#472961);
            background-color:transparent;
            border:0;
            padding:0;
            font-weight:700;
            display:flex;
            align-items:center;
            gap:0.5rem;
        }
        button.back span{
            display:flex;
            align-items:center;
            justify-content:center;
            width:1rem;
        }
        button.back svg{
            transform:rotate(0);
            transition:transform var(--transition) ease;
            width:1rem;
        }
        button.back:hover{
            text-decoration:underline;
        }

        button.back:hover svg{
            transform:rotate(15deg);
        }

        .card.disabled{
            background-color:  #e1e1e1;
            pointer-events: none;
            color: #555;
        }

        .disabled_message{
            font-style:italic;
            color:var(--allin-highlight-color,#472961);
        }

        @media (prefers-reduced-motion: reduce) {
            button.back svg{
                transform:none !important;
            }
        }

        `
        ];
}